<script setup lang="ts">
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import UserForm from '@/views/users/UserForm.vue'
import { usersApi, type CrmUser, type UserPayload } from '@/services/users'

/**
 * Edición de un usuario.
 *
 * La contraseña solo se manda si el campo se ha tocado. Si se mandara siempre,
 * cualquier guardado de un nombre dejaría al usuario sin poder entrar, y ese
 * fallo aparece días después, cuando alguien intenta iniciar sesión.
 */

const route = useRoute()
const router = useRouter()

const user = ref<CrmUser | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)
const submitting = ref(false)
const serverErrors = ref<Record<string, string[]>>({})

const model = ref({
  username: '',
  name: '',
  email: '',
  phone: '',
  attention_line: '',
  active: true,
  roles: [] as string[],
  password: '',
  password_confirmation: '',
})

/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 *
 * «Volver» empuja una entrada **nueva**, así que el historial no salva: la entrada anterior,
 * la que tenía los filtros, deja de ser la que se está usando.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('users.index')

function idFromRoute(): number {
  return Number(route.params.id)
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el usuario. Revisa tu conexión e inténtalo de nuevo.'
}

function extractFields(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as { response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } } }
  return maybeAxios.response?.data?.error?.details?.fields ?? {}
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = null

  try {
    const { data } = await usersApi.get(idFromRoute())
    user.value = data.data

    model.value = {
      username: data.data.username,
      name: data.data.name,
      email: data.data.email ?? '',
      phone: data.data.phone ?? '',
      attention_line: data.data.attention_line ?? '',
      active: data.data.active,
      roles: [...data.data.roles],
      password: '',
      password_confirmation: '',
    }
  } catch (cause: unknown) {
    loadError.value = extractMessage(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

watch(() => route.params.id, () => {
  void load()
})

async function onSubmit(payload: UserPayload): Promise<void> {
  submitting.value = true
  serverErrors.value = {}

  try {
    await usersApi.update(idFromRoute(), payload)
    await router.push({ name: 'users.show', params: { id: String(idFromRoute()) } })
  } catch (cause: unknown) {
    serverErrors.value = { ...extractFields(cause), general: [extractMessage(cause)] }
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header>
      <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
        Editar usuario
      </h1>
      <p
        v-if="user"
        class="mt-1 text-surface-500 dark:text-surface-400"
      >
        {{ user.name }} ({{ user.username }})
      </p>
    </header>

    <div
      v-if="loadError"
      class="flex flex-col items-center gap-3 py-12 text-center"
      role="alert"
    >
      <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
      <p class="font-medium text-surface-900 dark:text-surface-100">
        No se pudo cargar el usuario
      </p>
      <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
        {{ loadError }}
      </p>
      <Button
        label="Reintentar"
        icon="pi pi-refresh"
        severity="secondary"
        @click="load"
      />
    </div>

    <div
      v-else-if="loading"
      class="py-12 text-center text-surface-500"
      aria-busy="true"
    >
      <i class="pi pi-spin pi-spinner text-2xl" />
      <p class="mt-2 text-sm">
        Cargando usuario…
      </p>
    </div>

    <template v-else>
      <Message
        v-if="user !== null && !user.active"
        severity="warn"
        :closable="false"
        variant="simple"
      >
        Este usuario está inactivo: no puede iniciar sesión. Sus cotizaciones se
        conservan y siguen contando en el histórico de ventas.
      </Message>

      <AppCard>
        <UserForm
          v-model="model"
          :submitting="submitting"
          :server-errors="serverErrors"
          :is-edit="true"
          @submit="onSubmit"
          @cancel="volverAlListado"
        />
      </AppCard>
    </template>
  </div>
</template>
